import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { tasksAtom } from '../../../../../atoms/v0/taskAtoms';
import { componentThemeAtom } from '../../../../../atoms/v3/clockModeAtoms';
import { isToday } from 'date-fns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { ComponentTitle } from './ComponentTitle';

interface TodayEfficiencyAreaProps {
  currentTime: Date;
}

/**
 * 今日效率组件
 */
export const TodayEfficiencyArea: React.FC<TodayEfficiencyAreaProps> = ({
  currentTime
}) => {
  const { t } = useTranslation(['v3/clock']);
  const tasks = useAtomValue(tasksAtom);
  const componentTheme = useAtomValue(componentThemeAtom);

  // 计算今日任务统计
  const todayStats = React.useMemo(() => {
    const todayTasks = tasks.filter(task => {
      // 检查任务是否属于今天
      if (task.startAt && isToday(new Date(task.startAt))) return true;
      if (task.doneAt && isToday(new Date(task.doneAt))) return true;
      if (task.dueAt && isToday(new Date(task.dueAt))) return true;
      return false;
    });

    const completedTasks = todayTasks.filter(task => task.done);
    const totalTasks = todayTasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks.length / totalTasks) * 100) : 0;

    // 计算总时长（已完成任务的实际用时）
    const totalTimeSpent = completedTasks.reduce((total, task) => {
      if (task.startAt && task.doneAt) {
        const startTime = new Date(task.startAt);
        const endTime = new Date(task.doneAt);
        return total + Math.max(0, Math.floor((endTime.getTime() - startTime.getTime()) / 1000));
      }
      return total;
    }, 0);

    return {
      total: totalTasks,
      completed: completedTasks.length,
      completionRate,
      totalTimeSpent,
    };
  }, [tasks, currentTime]);

  // 计算今日进度数据（每2小时）
  const dailyProgressData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const data = [];
    for (let hour = 0; hour < 24; hour += 2) {
      const timePoint = new Date(today);
      timePoint.setHours(hour);

      const completedByThisTime = tasks.filter(task => {
        if (!task.doneAt || !isToday(new Date(task.doneAt))) return false;
        const doneTime = new Date(task.doneAt);
        return doneTime <= timePoint;
      }).length;

      data.push({
        name: `${hour.toString().padStart(2, '0')}:00`,
        completed: completedByThisTime,
        hour
      });
    }

    return data;
  }, [tasks]);

  // 根据主题获取颜色
  const getThemeColors = () => {
    if (componentTheme === 'dark') {
      return {
        gridStroke: '#3A3A3C',
        tickFill: '#8E8E93',
        tooltipBg: '#2C2C2E',
        tooltipText: '#FFFFFF'
      };
    } else {
      return {
        gridStroke: '#E5E5EA',
        tickFill: '#AEAEB2',
        tooltipBg: '#FFFFFF',
        tooltipText: '#000000'
      };
    }
  };

  const themeColors = getThemeColors();

  return (
    <div className="h-full w-full flex flex-col p-2">
      {/* 统一标题 */}
      <ComponentTitle
        title={t('v3/clock:components.todayEfficiency.title', '今日效率')}
        subtitle={`${todayStats.completionRate}% · ${todayStats.completed}/${todayStats.total}`}
      />

      {/* 图表 */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={dailyProgressData}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <defs>
              <linearGradient id="colorTodayCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#007AFF" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={themeColors.gridStroke}
              opacity={0.3}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 8, fill: themeColors.tickFill }}
              interval={1}
            />
            <YAxis hide={true} />
            <Tooltip
              contentStyle={{
                backgroundColor: themeColors.tooltipBg,
                border: 'none',
                borderRadius: '0.25rem',
                color: themeColors.tooltipText,
                fontSize: '0.625rem',
                padding: '0.25rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Area
              type="monotone"
              dataKey="completed"
              stroke="#007AFF"
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#colorTodayCompleted)"
              activeDot={{ r: 2, fill: "#007AFF", stroke: "white", strokeWidth: 1 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TodayEfficiencyArea;
